import {loggedFetchWithErrorReporting, loggedPaymentFetch} from '../utils/apiLogger';
import {BACKEND_URL} from '../config';

/**
 * Centralized API Service for all HTTP requests
 * This service handles all API calls with consistent error handling, logging, and authentication
 */
class ApiService {
    constructor() {
        this.baseURL = BACKEND_URL;
    }

    /**
     * Get authentication token from global function or cookies
     * @returns {string|null} JWT token or null if not available
     */
    getAuthToken() {
        if (window.getAuthToken) {
            return window.getAuthToken();
        }
        // Fallback to cookies if global function not available
        const Cookies = require('js-cookie');
        return Cookies.get(process.env.REACT_APP_COOKIE_NAME || 'prysm-registration');
    }

    /**
     * Get default headers for API requests
     * @param {Object} additionalHeaders - Additional headers to include
     * @returns {Object} Headers object
     */
    getDefaultHeaders(additionalHeaders = {}) {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            ...(token && {'Authorization': `Bearer ${token}`}),
            ...additionalHeaders
        };
    }

    /**
     * Handle authentication errors by resetting session
     * @returns {Promise<string|null>} New token or null if reset failed
     */
    async handleAuthError() {
        if (window.resetSession) {
            try {
                await window.resetSession();
                return this.getAuthToken();
            } catch (error) {
                console.error('[ApiService] Failed to reset session:', error);
                return null;
            }
        }
        return null;
    }

    // =============================================================================
    // AUTHENTICATION ENDPOINTS
    // =============================================================================

    /**
     * Create a new JWT token and session
     * @param {Object} options - Optional configuration for token creation
     * @returns {Promise<Object>} Response with token and success status
     */
    async createToken(options = {}) {
        try {
            const url = `${this.baseURL}/api/auth/create-token`;
            const requestBody = JSON.stringify({options});

            console.log('[ApiService] createToken request details:', {
                url,
                baseURL: this.baseURL,
                method: 'POST',
                body: requestBody,
                options
            });

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: requestBody
            });

            console.log('[ApiService] createToken fetch completed:', {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText,
                url: response.url
            });

            // Return the response object directly so clientPrysm can check response.ok and call response.json()
            return response;
        } catch (error) {
            console.error('[ApiService] createToken error details:', {
                message: error.message,
                name: error.name,
                stack: error.stack,
                type: typeof error,
                url: `${this.baseURL}/api/auth/create-token`
            });
            throw error;
        }
    }

    // =============================================================================
    // USER ENDPOINTS
    // =============================================================================

    /**
     * Get user data
     * @returns {Promise<Object>} User data response
     */
    async getUserData() {
        const url = `${this.baseURL}/api/user`;
        const headers = this.getDefaultHeaders();

        console.log('[ApiService] getUserData request details:', {
            url,
            baseURL: this.baseURL,
            method: 'GET',
            headers,
            hasAuthToken: !!this.getAuthToken()
        });

        try {
            const response = await loggedFetchWithErrorReporting(
                url,
                {
                    method: 'GET',
                    headers,
                    credentials: 'include'
                },
                'user_authentication'
            );

            console.log('[ApiService] getUserData response received:', {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText
            });

            return response;
        } catch (error) {
            console.error('[ApiService] getUserData error details:', {
                message: error.message,
                name: error.name,
                stack: error.stack,
                type: typeof error,
                url
            });
            throw error;
        }
    }

    /**
     * Register a new user
     * @param {Object} userData - User registration data
     * @returns {Promise<Object>} Registration response
     */
    async registerUser(userData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/user/register`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(userData)
            },
            'user_registration'
        );
    }

    /**
     * Submit user data
     * @param {Object} userData - User data to submit
     * @returns {Promise<Object>} Submission response
     */
    async submitUserData(userData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/user/submit-data`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(userData)
            },
            'user_data_submission'
        );
    }

    /**
     * Check username availability
     * @param {string} username - Username to check
     * @returns {Promise<Object>} Availability response
     */
    async checkUsernameAvailability(username) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/user/register/getusername`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify({username})
            },
            'username_availability_check'
        );
    }

    /**
     * Check email availability
     * @param {string} email - Email to check
     * @returns {Promise<Object>} Availability response
     */
    async checkEmailAvailability(email) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/user/register/getemail`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify({email})
            },
            'email_availability_check'
        );
    }

    // =============================================================================
    // PAYMENT ENDPOINTS
    // =============================================================================

    /**
     * Process payment with Square
     * @param {Object} paymentData - Payment data
     * @param {Object} paymentContext - Payment context for logging
     * @returns {Promise<Object>} Payment response
     */
    async processPayment(paymentData, paymentContext = {}) {
        // Generate idempotency key for payment
        const idempotencyKey = `payment_${paymentData.visitorUUID}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        return loggedPaymentFetch(
            `${this.baseURL}/api/payments/process`,
            {
                method: 'POST',
                headers: {
                    ...this.getDefaultHeaders(),
                    'Idempotency-Key': idempotencyKey
                },
                credentials: 'include',
                body: JSON.stringify({
                    ...paymentData,
                    idempotencyKey
                })
            },
            paymentContext
        );
    }

    /**
     * Process Square customer data
     * @param {Object} customerData - Customer registration data
     * @returns {Promise<Object>} Customer processing response
     */
    async processSquareCustomer(customerData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/square/customer/process`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(customerData)
            },
            'square_customer_processing'
        );
    }

    /**
     * Check promotional code validity
     * @param {string} promoCode - The promotional code to validate
     * @param {string} email - User's email address
     * @param {string} gender - User's gender
     * @returns {Promise<Object>} Promo code validation response
     */
    async checkPromoCode(promoCode, email, gender) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/square/customer/checkpromocode`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify({promoCode, email, gender})
            },
            'promo_code_validation'
        );
    }

    /**
     * Process free registration when promo code covers full cost
     * @param {Object} registrationData - Free registration data
     * @returns {Promise<Object>} Free registration response
     */
    async processFreeRegistration(registrationData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/payments/process-free`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(registrationData)
            },
            'free_registration_processing'
        );
    }

    // =============================================================================
    // IMAGE ENDPOINTS
    // =============================================================================

    /**
     * Get image status
     * @returns {Promise<Object>} Image status response
     */
    async getImageStatus() {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/images/status`,
            {
                method: 'GET',
                headers: this.getDefaultHeaders(),
                credentials: 'include'
            },
            'image_status_check'
        );
    }

    /**
     * Upload selfie image for verification
     * @param {File} selfieFile - The selfie image file
     * @returns {Promise<Object>} Upload response
     */
    async uploadSelfie(selfieFile) {
        const formData = new FormData();
        formData.append('selfie', selfieFile);

        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/images/upload-selfie`,
            {
                method: 'POST',
                credentials: 'include',
                body: formData
                // Note: Don't set Content-Type header, let browser set it with boundary
            },
            'selfie_upload'
        );
    }

    /**
     * Check specific image processing status by ID
     * @param {number} imageId - The image ID to check
     * @returns {Promise<Object>} Status response
     */
    async checkImageStatus(imageId) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/images/status/${imageId}`,
            {
                method: 'GET',
                headers: this.getDefaultHeaders(),
                credentials: 'include'
            },
            'check_image_status'
        );
    }

    /**
     * Upload license images for storage
     * @param {File} frontFile - The front license image file
     * @param {File} backFile - The back license image file
     * @returns {Promise<Object>} Upload response
     */
    async uploadLicense(frontFile, backFile) {
        const formData = new FormData();
        formData.append('front', frontFile);
        formData.append('back', backFile);

        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/images/upload-license`,
            {
                method: 'POST',
                credentials: 'include',
                body: formData
                // Note: Don't set Content-Type header, let browser set it with boundary
            },
            'license_upload'
        );
    }

    // =============================================================================
    // SMS ENDPOINTS
    // =============================================================================

    /**
     * Send SMS verification code
     * @param {Object} smsData - SMS data including phone number
     * @returns {Promise<Object>} SMS response
     */
    async sendSMSCode(smsData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/sms/send-code`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(smsData)
            },
            'sms_code_send'
        );
    }

    /**
     * Verify SMS code
     * @param {Object} verificationData - Verification data including code
     * @returns {Promise<Object>} Verification response
     */
    async verifySMSCode(verificationData) {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/sms/verify-code`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(verificationData)
            },
            'sms_code_verification'
        );
    }

    /**
     * Send registration notice via SMS
     * @param {Object} registrationResponse - Registration response data from local storage
     * @returns {Promise<Object>} SMS response
     */
    async sendRegistrationNotice(registrationResponse = null) {
        const requestBody = registrationResponse ? {registrationResponse} : {};

        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/sms/send-registration-notice`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(requestBody)
            },
            'registration_notice_sms'
        );
    }

    // =============================================================================
    // SESSION ENDPOINTS
    // =============================================================================

    /**
     * Reset user session
     * @returns {Promise<Object>} Reset response
     */
    async resetSession() {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/session/reset`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include'
            },
            'session_reset'
        );
    }

    // =============================================================================
    // CLIENT LOGGING ENDPOINT
    // =============================================================================

    /**
     * Send clientPrysm logs to server
     * @param {Array} logs - Array of log entries
     * @param {string} clientVersion - Client version
     * @param {string} timestamp - Timestamp
     * @param {Object} localStorageData - All local storage data organized by store name and key
     * @returns {Promise<Object>} Log response
     */
    async sendClientLogs(logs, clientVersion, timestamp, localStorageData = null) {
        const requestBody = {
            logs,
            clientVersion,
            timestamp
        };

        // Include local storage data if provided
        if (localStorageData) {
            requestBody.localStorageData = localStorageData;
        }

        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/client-log`,
            {
                method: 'POST',
                headers: this.getDefaultHeaders(),
                credentials: 'include',
                body: JSON.stringify(requestBody)
            },
            'client_logging'
        );
    }

    // =============================================================================
    // UTILITY METHODS
    // =============================================================================

    /**
     * Make a generic authenticated request with automatic retry on auth failure
     * @param {string} endpoint - API endpoint (without base URL)
     * @param {Object} options - Fetch options
     * @param {string} context - Context for logging
     * @param {boolean} retryOnAuthFailure - Whether to retry on auth failure
     * @returns {Promise<Object>} Response
     */
    async makeAuthenticatedRequest(endpoint, options = {}, context = 'api_call', retryOnAuthFailure = true) {
        try {
            // Ensure headers are set
            const requestOptions = {
                ...options,
                headers: this.getDefaultHeaders(options.headers),
                credentials: 'include'
            };

            return await loggedFetchWithErrorReporting(
                `${this.baseURL}${endpoint}`,
                requestOptions,
                context
            );
        } catch (error) {
            // Don't automatically reset session on auth errors - just throw the error
            console.log('[ApiService] Authentication error detected, but not resetting session automatically:', error.status);
            throw error;
        }
    }

    /**
     * Upload file with authentication
     * @param {string} endpoint - Upload endpoint
     * @param {FormData} formData - Form data containing file
     * @param {string} context - Context for logging
     * @returns {Promise<Object>} Upload response
     */
    async uploadFile(endpoint, formData, context = 'file_upload') {
        const token = this.getAuthToken();
        const headers = {};

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return loggedFetchWithErrorReporting(
            `${this.baseURL}${endpoint}`,
            {
                method: 'POST',
                headers,
                credentials: 'include',
                body: formData
            },
            context
        );
    }

    // =============================================================================
    // SUPPORT ENDPOINTS
    // =============================================================================

    /**
     * Get FAQ data from support endpoint
     * @returns {Promise<Object>} FAQ response with questions and answers
     */
    async getFAQ() {
        return loggedFetchWithErrorReporting(
            `${this.baseURL}/api/support/faq`,
            {
                method: 'GET',
                headers: this.getDefaultHeaders(),
                credentials: 'include'
            },
            'faq_request'
        );
    }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
